import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { es } from 'date-fns/locale';
import { getCookie, setCookieExpiringToday } from 'helpers/utils/cookies';
import { useSegmentConfigStore } from 'stores/segmentConfigStore';
import { useCustomMessagePopupStore } from 'stores/useCustomMessagePopupStore';
import { useDashboardBannerStore } from 'stores/useDashboardBannerStore';
import { useDateTimeRangeStore } from 'stores/useDateTimeRangeStore';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RemoteConfigContextProvider } from '@context';
import { useErrorSnackbar } from '@hooks';
import { GlobalErrorAlert, CustomMessagePopup } from '@molecules';
import { HotjarScript } from '@organisms';
import { options } from '@utils';
import { addErrorInterceptor, removeErrorInterceptor } from '@api/interceptors';
import AuthProvider from '@config/auth';
import SWR from '@config/swr';
import '@config/i18n';
import { AppRouter } from './navigation';
import { theme } from './theme';
import './App.css';

/**
 * Main component for the app.
 * @returns
 */
function App() {
  const { showErrorSnackbar, ErrorSnackbar } = useErrorSnackbar();
  const loadSegmentConfig = useSegmentConfigStore((state) => state.loadConfig);
  const loadBannersConfig = useDashboardBannerStore((state) => state.loadConfig);
  const loadDateTimeRangeConfig = useDateTimeRangeStore((state) => state.loadConfig);
  const { loadConfig: loadCustomMessageConfig, config: customMessageConfig } =
    useCustomMessagePopupStore();

  const [isCustomMessageVisible, setIsCustomMessageVisible] = useState(false);
  const customMessagePopup = customMessageConfig?.custom_message_popup;
  const CUSTOM_MESSAGE_COOKIE_KEY = 'MessagePopup';

  /** Verify show Popup today */
  const hasPopupBeenShownToday = () => {
    return getCookie(CUSTOM_MESSAGE_COOKIE_KEY) === 'true';
  };

  /** Set popup today */
  const markPopupAsShownToday = () => {
    setCookieExpiringToday(CUSTOM_MESSAGE_COOKIE_KEY, 'true');
  };

  useEffect(() => {
    addErrorInterceptor(showErrorSnackbar);
    loadSegmentConfig();
    loadBannersConfig();
    loadDateTimeRangeConfig();
    loadCustomMessageConfig();

    return () => {
      removeErrorInterceptor();
    };
  }, [
    loadBannersConfig,
    loadCustomMessageConfig,
    loadDateTimeRangeConfig,
    loadSegmentConfig,
    showErrorSnackbar,
  ]);

  useEffect(() => {
    if (customMessagePopup?.enabled && !hasPopupBeenShownToday()) {
      setIsCustomMessageVisible(true);
    }
  }, [customMessagePopup?.enabled]);

  return (
    <SWR>
      <RemoteConfigContextProvider>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <ToastContainer {...options} />
              <HotjarScript />
              <AppRouter />
              <ErrorSnackbar />
              <GlobalErrorAlert />
              {customMessageConfig && (
                <CustomMessagePopup
                  isOpen={isCustomMessageVisible}
                  onClose={() => {
                    setIsCustomMessageVisible(false);
                    markPopupAsShownToday();
                  }}
                  title={customMessagePopup.title}
                  message={customMessagePopup.message}
                  showCloseButton={customMessagePopup.showCloseButton}
                  showConfirmButton={customMessagePopup.showConfirmButton}
                  confirmButtonText={customMessagePopup.confirmButtonText}
                />
              )}
            </LocalizationProvider>
          </ThemeProvider>
        </AuthProvider>
      </RemoteConfigContextProvider>
    </SWR>
  );
}
export default App;
